import Input from "@/app/components/ui/Inputs/Input";
import GeoSearchSelectMenu from "@/app/components/ui/SelectMenus/GeoSearchSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IRotaState } from "@/app_redux/reducers/slices/auth/rota_slice";
import clsx from "clsx";
import { useEffect } from "react";
import { INewAddressBlockProps } from "@components/routes/auth_routes/CustomerProfile/customer_profile_interface";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

const NewAddressBlock: React.FC<INewAddressBlockProps> = ({ formHook }) => {
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ROTA", "id");
  const { routeState } = useRouteInfo(routeId);

  const { cities: cityList } = routeState.state as IRotaState;

  const { indianStates, address_labels: addressLabels } = useArrayConstants();

  const fetchCities = useOrgCitiesListAPI("ROTA");

  const cityOrgItem = useSelectMenuReducer(cityList, "name", "id");

  // RBAC: CUSTOMER_ADDRESS_BLUECITY_VARIANT — legacy role: clinic_ops_bluecity
  const isBluecity = useFeatureAccess("CUSTOMER_ADDRESS_BLUECITY_VARIANT");

  useEffect(() => {
    fetchCities({
      withClinics: false,
      staffCategoryId: "2",
      withClinicProviders: false,
      clinicOptions: {
        selectFirst: true,
      },
    });
  }, []);

  const { forOnlyNumber, forOnlyAlphabet, forLatitude, forLongitude } = useFormValidations();

  useEffect(() => {
    formHook.setFocus("address_2");
  }, []);

  return (
    <div className="flex flex-col gap-2 pt-6 pb-6 px-2 rounded-md  ">
      <div className="col-span-2 w-full">
        <Input
          placeholder="Enter H NO., Street Name, Landmark"
          className="text-xs w-full"
          fieldError={formHook?.formState?.errors?.address_1}
          register={formHook.register(`address_1`, {
            required: false,
          })}
          errorMessages={[{ type: "required", message: "Address 1 is required" }]}
        />
      </div>
      <div className="col-span-1"></div>
      <div className="col-span-2 w-full">
        <GeoSearchSelectMenu
          autoComplete="off"
          autoCorrect="false"
          showDropdownWhenNoData
          withClearAction
          onClear={() => {
            formHook.setValue(`address_2`, "");
            formHook.setValue(`city`, "");
            formHook.setValue(`pincode`, "");
            formHook.setValue(`state`, "");
            formHook.setValue(`latitude`, null);
            formHook.setValue(`longitude`, null);
          }}
          onSelectItem={(item) => {
            if (item) {
              formHook.setValue(`address_2`, item.state.formattedAddress);
              formHook.setValue(`city`, item.state.city !== "" ? item.state.city : null);
              formHook.setValue(`pincode`, item.state.pincode !== "" ? item.state.pincode : null);
              formHook.setValue(`state`, item.state.state !== "" ? item.state.state : null);
              formHook.setValue(`latitude`, item.state.lat !== "" ? item.state.lat.toString() : null);
              formHook.setValue(`longitude`, item.state.long !== "" ? item.state.long.toString() : null);
            }
          }}
          errorMessages={[{ message: "", type: "required" }]}
          fieldError={formHook.formState.errors[`address_2`]}
          register={formHook.register(`address_2`, {
            required: !isBluecity,
          })}
          showTypedErrors={true}
          placeholder="Search Google Location"
        />
      </div>
      <div className="col-span-2 w-full gap-2 grid grid-cols-2">
        <div className="col-span-1 ">
          <Input
            className="w-full text-xs"
            placeholder="Latitude"
            fieldError={formHook.formState.errors[`latitude`]}
            register={formHook.register(`latitude`, {
              ...forLatitude.validations,
              required: !isBluecity,
            })}
            errorMessages={[forLatitude.errors, { type: "required", message: "" }]}
          />
        </div>
        <div className="col-span-1 ">
          <Input
            className="w-full text-xs"
            placeholder="Longitude"
            fieldError={formHook.formState.errors[`longitude`]}
            register={formHook.register(`longitude`, {
              ...forLongitude.validations,
              required: !isBluecity,
            })}
            errorMessages={[forLongitude.errors, { type: "required", message: "" }]}
          />
        </div>
      </div>
      <div className="col-span-2 w-full gap-2 grid grid-cols-3 items-center">
        <div className="col-span-1 ">
          <Input
            className="w-full text-xs"
            placeholder="Pincode"
            maxLength={6}
            fieldError={formHook.formState.errors[`pincode`]}
            register={formHook.register(`pincode`, {
              ...forOnlyNumber.validations,
              required: !isBluecity,
              maxLength: 6,
            })}
            errorMessages={[
              forOnlyNumber.errors,
              { type: "required", message: "" },
              { type: "maxLength", message: "Too long" },
            ]}
          />
        </div>
        <div className="col-span-1 ">
          <SearchableSelectMenu
            onSelectItem={(item) => {
              if (item) {
                formHook.setValue(`city`, item.title);
              }
            }}
            fieldError={formHook.formState.errors[`city`]}
            register={formHook.register(`city`, {
              ...forOnlyAlphabet.validations,
              required: !isBluecity,
            })}
            errorMessages={[forOnlyAlphabet.errors, { type: "required", message: "" }]}
            selectItems={cityOrgItem}
            placeholder="Select City"
            showTooltips={true}
            showTypedErrors={true}
            showDropdownIcon={true}
            defaultSelected={cityOrgItem?.filter((oc) => oc.title === formHook.watch(`city`))[0]}
            listBoxClassName="w-full"
            className="text-gray-800 "
            containerClassName="w-full"
          />
        </div>
        <div className="col-span-1 w-full">
          <SearchableSelectMenu
            onSelectItem={(item) => {
              if (item) {
                formHook.setValue(`state`, item.title);
              }
            }}
            fieldError={formHook.formState.errors[`state`]}
            register={formHook.register(`state`, {
              ...forOnlyAlphabet.validations,
              required: !isBluecity,
            })}
            errorMessages={[forOnlyAlphabet.errors, { type: "required", message: "" }]}
            selectItems={indianStates}
            placeholder="Select State"
            showTooltips={false}
            showTypedErrors={true}
            showDropdownIcon={true}
            defaultSelected={indianStates?.filter((oc) => oc.title === formHook.watch(`state`))[0]}
            listBoxClassName="w-full"
            className="text-gray-800 "
            containerClassName="w-full"
          />
        </div>
      </div>

      <div className="col-span-1">
        <p></p>
      </div>
      <div className="col-span-2 w-full gap-4 grid grid-cols-3">
        {addressLabels.length > 0 &&
          addressLabels.map((adL, idx) => {
            return (
              <div key={`${adL.label}:${idx}`} className="col-span-1 w-full mt-1">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    formHook.setValue(`label`, adL?.label);
                  }}
                  className={clsx(
                    "border   opacity-100 w-full py-2 rounded-md   ",
                    formHook.watch("label") === adL.label
                      ? "border-[#193a6a] opacity-100 border focus:border-gray-400 hover:bg-[rgba(128,141,160,0.1)] hover:border-gray-200 bg-gray-100"
                      : "text-gray-400 border-[rgba(128, 141, 160, 0.20)] focus:border-[#193a6a] hover:bg-[rgba(128,141,160,0.1)] hover:border-gray-200",
                  )}
                  id="address_home"
                >
                  {adL.value}
                </button>
              </div>
            );
          })}
        <div className="-mt-3">
          <Input
            type="text"
            register={formHook.register(`label`, { required: false })}
            fieldError={formHook?.formState?.errors?.label}
            // errorMessages={[{ type: "required", message: "Address type is required." }]}
            errorMessages={[]}
            hidden
          />
        </div>
      </div>
    </div>
  );
};

export default NewAddressBlock;
